/**
 * Explore Our Online Programs - Variant 3 (Chandigarh University)
 *
 * Two-tone heading ("Explore Our" + highlighted "Online Programs"), an optional
 * Bachelors / Masters segmented control, and a wrapping row of program cards
 * whose white body overlaps the image. Each card has a corner badge
 * ("POPULAR" / "NEW") and flips to a solid hover color; a "View More / View
 * Less" button toggles the full list when there are more than 3 cards.
 *
 * Reuses the shared program_cards / program_cards_category / program_cards_card
 * tables. Section hover colors + card badge fields are variant-3 only columns.
 */

export const PROGRAM_CARDS_3_CONSTANTS = {
  DEFAULT_ROW: {
    id: "1",
    title: "",
    subtitle: "",
    badge_text: "",
    button_text: "Explore",
    priority: 1,
  },

  PROGRAM_CARDS_FIELDS_CONFIG: [
    {
      fieldName: "desktop_background_color",
      label: "Desktop Background Color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "mobile_background_color",
      label: "Mobile Background Color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "pre_title",
      label: "Heading — first part (max 20 characters)",
      value: "Explore Our",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Explore Our",
      required: false,
      defaultValue: "Explore Our",
    },
    {
      fieldName: "pre_title_color",
      label: "Heading — first part color",
      value: "#171719",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #171719",
      required: false,
      defaultValue: "#171719",
    },
    {
      fieldName: "title",
      label: "Heading — highlighted part (max 20 characters)",
      value: "Online Programs",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Online Programs",
      required: false,
      defaultValue: "Online Programs",
    },
    {
      fieldName: "title_start_color",
      label: "Heading — highlighted part color",
      value: "#E1473F",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #E1473F",
      required: false,
      defaultValue: "#E1473F",
    },
    {
      fieldName: "use_categories",
      label: "Show category tabs (e.g. Bachelors / Masters)",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "card_background_color",
      label: "Card Background Color (default)",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "card_hover_background_color",
      label: "Card Hover Background Color",
      value: "#E1473F",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #E1473F",
      required: false,
      defaultValue: "#E1473F",
    },
    {
      fieldName: "card_hover_text_color",
      label: "Card Hover Text Color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "cta_text",
      label: '"View More" button text',
      value: "View More",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. View More",
      required: false,
      defaultValue: "View More",
    },
    {
      fieldName: "cta_text_color",
      label: '"View More" text color',
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "cta_background_start_color",
      label: '"View More" background color',
      value: "#E1473F",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #E1473F",
      required: false,
      defaultValue: "#E1473F",
    },
    {
      fieldName: "cta_icon",
      label: '"View More" icon',
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload button icon (optional)",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "cta_icon_alt",
      label: '"View More" icon alt text',
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter icon alt text",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "status",
      label: "Status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable/Disable",
      required: false,
      defaultValue: true,
    },
  ],

  CATEGORY_FIELDS_CONFIG: [
    {
      fieldName: "name",
      label: "Category Name",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Masters",
      required: true,
      defaultValue: "",
    },
    {
      fieldName: "order_index",
      label: "Display Order",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "Enter Display Order",
      required: true,
      defaultValue: 1,
    },
    {
      fieldName: "is_default",
      label: "Default Category (shows all programs)",
      value: false,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Show all programs",
      required: false,
      defaultValue: false,
    },
    {
      fieldName: "text_color",
      label: "Inactive Tab Text Color",
      value: "#171719",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #171719",
      required: false,
      defaultValue: "#171719",
    },
    {
      fieldName: "active_text_color",
      label: "Active Tab Text Color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "active_background_color",
      label: "Active Tab Background Color",
      value: "#E1473F",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #E1473F",
      required: false,
      defaultValue: "#E1473F",
    },
  ],

  CARD_TABLE_FIELDS_CONFIG: [
    {
      header: "Image",
      fieldName: "image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload Card Image",
      required: false,
      defaultValue: "",
    },
    {
      header: "Image Alt",
      fieldName: "image_alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter Image Alt",
      required: false,
      defaultValue: "",
    },
    {
      header: "Badge Text (e.g. POPULAR / NEW — empty hides it)",
      fieldName: "badge_text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Leave empty to hide badge",
      required: false,
      defaultValue: "",
    },
    {
      header: "Badge Text Color",
      fieldName: "badge_text_color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      header: "Badge Background Color",
      fieldName: "badge_background_color",
      value: "#FFA405",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #FFA405 (POPULAR) / #16A34A (NEW)",
      required: false,
      defaultValue: "#FFA405",
    },
    {
      header: "Title (max 30 characters)",
      fieldName: "title",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Online MBA",
      required: true,
    },
    {
      header: "Title Color",
      fieldName: "title_color",
      value: "#171719",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #171719",
      required: false,
      defaultValue: "#171719",
    },
    {
      header: "Subtitle (max 2 lines)",
      fieldName: "subtitle",
      value: "",
      valueType: "string",
      filedType: "TEXT_AREA",
      placeholder: "e.g. Master of Business Administration in Business Analytics",
      required: false,
      defaultValue: "",
    },
    {
      header: "Subtitle Color",
      fieldName: "subtitle_color",
      value: "#6B7280",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #6B7280",
      required: false,
      defaultValue: "#6B7280",
    },
    {
      header: 'Explore link text (e.g. "Explore MBA")',
      fieldName: "button_text",
      value: "Explore",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Explore MBA",
      required: false,
      defaultValue: "Explore",
    },
    {
      header: "Explore link color",
      fieldName: "button_text_color",
      value: "#E1473F",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #E1473F",
      required: false,
      defaultValue: "#E1473F",
    },
    {
      header: "Explore link icon (optional)",
      fieldName: "button_icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Optional arrow icon",
      required: false,
      defaultValue: "",
    },
    {
      header: "Explore link icon alt",
      fieldName: "button_icon_alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Alt text",
      required: false,
      defaultValue: "",
    },
    {
      header: "Link URL (course page)",
      fieldName: "course_url",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "https://… or /slug",
      required: false,
      defaultValue: "",
    },
    {
      header: "Priority (display order within tab)",
      fieldName: "priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      required: true,
      defaultValue: 1,
    },
  ],
};
